import React, { useState } from "react";
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import AppTheme from '../shared-theme/AppTheme';
import Banner from "../components/Banner";
import Box from '@mui/material/Box';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const GradientBackground = styled('div')({
  position: 'fixed',
  inset: 0,
  background: 'radial-gradient(circle at top left, #041C2C 0%, #22143C 100%)',
  zIndex: -1,
});

const SettingsContainer = styled(Stack)(({ theme }) => ({
  minHeight: '100dvh',
  marginTop: '50px',
  padding: theme.spacing(2),
  overflowY: 'auto',
  position: 'relative',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
}));

const PrivacyPolicy: React.FC = () => {
  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <GradientBackground />
      <Banner />
      <SettingsContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
          >
            Privacy Policy
          </Typography>
          <Typography variant="h5" gutterBottom>Last Updated: 07/04/2025</Typography>

          <Typography variant="body1" >
            The privacy of your data—and it is your data, not ours!—is a big deal to us. In this policy, we lay out: what data we collect and why; how your data is handled; and your rights with respect to your data. We do not sell your data.
          </Typography>
          <Typography variant="body1" >
            This policy applies to all products built and maintained by Event Spy.
          </Typography>
          <Typography variant="body1" >
            This policy applies to our handling of information about site visitors and prospective customers. We refer collectively to these categories of individuals as "you" throughout this policy.
          </Typography>
          <Typography variant="body1" >
            However, this policy does not cover information about a customer’s end users that Event Spy receives from a customer, or otherwise processes on a customer’s behalf, in connection with the services provided by Event Spy to the customer pursuant to an applicable services agreement (including the content of messages of customer end users ("End User Communications")). Event Spy processes End User Communications under the instructions of the relevant customer, which is the "data controller" or "business" (or occupies a similar role as defined in applicable privacy laws), as described in the applicable services agreement between such customer and Event Spy. Event Spy’s obligations as a "data processor" or "service provider" with respect to such information are defined in such services agreement and applicable data protection addendum and are not made part of this policy.
          </Typography>
          <Typography variant="body1" paragraph>
            If you are a customer’s end user and you have questions about how your information is collected and processed through the services, please contact the organization who has provided your information to us for more information.
          </Typography>

          <Typography variant="h3" >What we collect and why</Typography>
          <Typography variant="body1" paragraph>
            Our guiding principle is to collect only what we need. Here’s what that means in practice:
          </Typography>

          <Typography variant="h5" >Identity and access</Typography>
          <Typography variant="body1" >
            When you sign up for an Event Spy product, we ask for identifying information such as your email address. That’s so you can setup your new account, and we can send you product updates and other essential information.
          </Typography>
          <Typography variant="body1" paragraph>
            We’ll never sell your personal information to third parties.
          </Typography>

          <Typography variant="h5" >Billing information</Typography>
          <Typography variant="body1" paragraph>
            If you sign up for a paid Event Spy product, you will be asked to provide your payment information and billing address. Credit card information is submitted directly to our payment processor and doesn’t hit Event Spy’s servers. We store a record of the payment transaction, for purposes of account history, invoicing, and billing support.
          </Typography>

          <Typography variant="h5" >Website interactions</Typography>
          <Typography variant="body1" paragraph>
            We collect information about your browsing activity for analytics and statistical purposes such as conversion rate testing and experimenting with new product designs. This includes, for example, your browser and operating system versions, your IP address, which web pages you visited and how long they took to load, and which website referred you to us. If you have an account and are signed in, these web analytics data are tied to your IP address and user account until your account is no longer active.
          </Typography>

          <Typography variant="h5">Voluntary correspondence</Typography>
          <Typography variant="body1" >
            When you email Event Spy with a question or to ask for help, we keep that correspondence, including your email address, so that we have a history of past correspondence to reference if you reach out in the future.
          </Typography>
          <Typography variant="body1" paragraph>
            We also store information you may volunteer, for example, written responses to surveys. If you agree to a customer interview, we may ask for your permission to record the conversation for future reference or use. We will only do so with your express consent.
          </Typography>

          <Typography variant="h5">Company subprocessers</Typography>
          <Typography variant="body1" >
            We as a company use third-party software that may process your information under certain circumstances.
          </Typography>
          <Box component="ul" sx={{ marginLeft: '-20px' }}>
            <Typography variant="body1">
              <li><a href="https://www.namecheap.com/legal/general/privacy-policy/">Namecheap</a> - DNS.</li>
            </Typography>
            <Typography variant="body1">
              <li><a href="https://stripe.com/privacy">Stripe</a> - Payment processing.</li>
            </Typography>
            <Typography variant="body1" paragraph>
              <li><a href="https://aws.amazon.com/privacy/?nc1=f_pr">AWS</a> - Hosting.</li>
            </Typography>
          </Box>

          <Typography variant="h5">When we access or disclose your information</Typography>
          <Typography variant="body1" >
            <b>To provide products or services you’ve requested.</b> We use some third-party subprocessors to help run our applications and provide the Services to you. You can view the third-party subprocessors above.
          </Typography>
          <Typography variant="body1">
            <b>To help you troubleshoot or squash a software bug, with your permission.</b> If at any point we need to access your content to help you with a support case, we will ask for your consent before proceeding.
          </Typography>
          <Typography variant="body1">
            <b>To investigate, prevent, or take action regarding our TOS.</b> Accessing a customer’s account when investigating potential abuse is a measure of last resort. We want to protect the privacy and safety of both our customers and the people reporting issues to us, and we do our best to balance those responsibilities throughout the process. If we discover you are using our products for a restricted purpose, we will take action as necessary, including notifying appropriate authorities where warranted.
          </Typography>
          <Typography variant="body1" >
            <b>Aggregated and de-identified data.</b> We may aggregate and/or de-identify information collected through the services. We may use de-identified or aggregated data for any purpose, including marketing or analytics.
          </Typography>
          <Typography variant="body1" >
            <b>When required under applicable law.</b> Event Spy is a U.K. company and all data infrastructure are located in the U.K.
          </Typography>
          <Box component="ul" sx={{ marginLeft: '-20px' }}>
            <Typography variant="body1" paragraph>
              <li>Requests for user data. Our policy is to not respond to government requests for user data unless we are compelled by legal process or in limited circumstances in the event of an emergency request. However, if U.K. law enforcement authorities have the necessary warrant, criminal subpoena, or court order requiring us to disclose data, we must comply. Likewise, we will only respond to requests from government authorities outside the U.K. if compelled by the U.K. government through procedures outlined in a mutual legal assistance treaty or agreement. It is Event Spy’s policy to notify affected users before we disclose data unless we are legally prohibited from doing so, and except in some emergency cases.</li>
            </Typography>
            <Typography variant="body1" paragraph>
              <li>Preservation requests. Similarly, Event Spy policy is to comply with requests to preserve data only if compelled for civil matters. We do not disclose preserved data unless required by law or compelled by a court order that we choose not to appeal. Furthermore, unless we receive a proper warrant, court order, or subpoena before the required preservation period expires, we will destroy any preserved copies of customer data at the end of the preservation period. </li>
            </Typography>
            <Typography variant="body1" >
              <li>If we are audited by a tax authority, we may be required to disclose billing-related information. If that happens, we will disclose only the minimum needed, such as billing addresses and tax exemption information.</li>
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Finally, if Event Spy is acquired by or merges with another company — we don’t plan on that, but if it happens — we’ll notify you well before any of your personal information is transferred or becomes subject to a different privacy policy.
          </Typography>

          <Typography variant="h5">Your rights with respect to your information</Typography>
          <Typography variant="body1" >
            At Event Spy, we strive to apply the same data rights to all customers, regardless of their location. Some of these rights include:
          </Typography>
          <Box component="ul" sx={{ marginLeft: '-20px' }}>
            <Typography variant="body1" paragraph>
              <li><b>Right to Know.</b> You have the right to know what personal information is collected, used, shared or sold. We outline both the categories and specific bits of data we collect, as well as how they are used, in this privacy policy.</li>
            </Typography>
            <Typography variant="body1" paragraph>
              <li><b>Right of Access.</b> This includes your right to access the personal information we gather about you, and your right to obtain information about the sharing, storage, security and processing of that information.</li>
            </Typography>
            <Typography variant="body1" paragraph>
              <li><b>Right to Correction.</b> You have the right to request correction of your personal information.</li>
            </Typography>
            <Typography variant="body1" paragraph>
              <li><b>Right to Erasure / “To Be Forgotten”.</b> This is your right to request, subject to certain limitations under applicable law, that your personal information be erased from our possession and, by extension, from all of our service providers. Fulfillment of some data deletion requests may prevent you from using Event Spy’s services because our applications may then no longer work. In such cases, a data deletion request may result in closing your account.</li>
            </Typography>
            <Typography variant="body1" paragraph>
              <li><b>Right to Complain.</b> You have the right to make a complaint regarding our handling of your personal information with the appropriate supervisory authority.</li>
            </Typography>
            <Typography variant="body1" paragraph>
              <li><b>Right to Restrict Processing.</b> This is your right to request restriction of how and why your personal information is used or processed, including opting out of sale of your personal information.</li>
            </Typography>
            <Typography variant="body1" paragraph>
              <li><b>Right to Object.</b> You have the right, in certain situations, to object to how or why your personal information is processed.</li>
            </Typography>
            <Typography variant="body1" paragraph>
              <li><b>Right to not Be Subject to Automated Decision-Making.</b> You have the right to object to and prevent any decision that could have a legal or similarly significant effect on you from being made solely based on automated processes. This right is limited if the decision is necessary for performance of any contract between you and us, is allowed by applicable law, or is based on your explicit consent.</li>
            </Typography>
            <Typography variant="body1">
              <li><b>Right to Non-Discrimination.</b> We do not and will not charge you a different amount to use our products, offer you different discounts, or give you a lower level of customer service because you have exercised your data privacy rights. However, the exercise of certain rights may, by virtue of your exercising those rights, prevent you from using our Services.</li>
            </Typography>
          </Box>
          <Typography variant="body1">
            Many of these rights can be exercised by signing in and updating your account information. Please note that certain information may be exempt from such requests under applicable law. For example, we need to retain certain information in order to provide our services to you.
          </Typography>
          <Typography variant="body1" paragraph>
            Depending on applicable law, you may have the right to appeal our decision to deny your request, if applicable. We will provide information about how to exercise that right in our response denying the request. You also have the right to lodge a complaint with a supervisory authority. If you are in the EU or UK, you can contact your data protection authority to file a complaint or learn more about local privacy laws.
          </Typography>

          <Typography variant="h5">How we secure your data</Typography>
          <Typography variant="body1" paragraph>
            All data is encrypted via SSL/TLS when transmitted from our servers to your browser. The database backups are also encrypted. In addition, we go to great lengths to secure your data at rest.
          </Typography>

          <Typography variant="h5">Data retention</Typography>
          <Typography variant="body1" paragraph>
            We keep your information for the time necessary for the purposes for which it is processed. The length of time for which we retain information depends on the purposes for which we collected and use it and your choices, after which time we may delete and/or aggregate it. We may also retain and use this information as necessary to comply with our legal obligations, resolve disputes, and enforce our agreements. Through this policy, we have provided specific retention periods for certain types of information.
          </Typography>

          <Typography variant="h5">Location of site and data</Typography>
          <Typography variant="body1" paragraph>
            Our products and other web properties are operated in the United Kingdom. If you are located in the European Union, U.S., or elsewhere outside of the United Kingdom, please be aware that any information you provide to us will be transferred to and stored in the United Kingdom. By using our websites or Services and/or providing us with your personal information, you consent to this transfer.
          </Typography>

          <Typography variant="h5">Changes and questions</Typography>
          <Typography variant="body1">
            We may update this policy as needed to comply with relevant regulations and reflect any new practices. Whenever we make a significant change to our policies, we will refresh the date at the top of this page and take any other appropriate steps to notify users.
          </Typography>
          <Typography variant="body1" paragraph>
            Have any questions, comments, or concerns about this privacy policy, your data, or your rights with respect to your information? Please get in touch by emailing us at <a href="mailto:support@event-spy.com">support@event-spy.com</a> and we’ll be happy to try to answer them!
          </Typography>

        </Card>
      </SettingsContainer>
    </AppTheme >
  );
};

export default PrivacyPolicy;
